import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import useDebouncedValue from '../hooks/useDebouncedValue';
import {
  fetchActiveServiceById,
  mergeServiceSearchResults,
  searchActiveServices
} from '../utils/catalogSearch';

const NEW_SENT = '__new_service__';

function serviceOptionLabel(s) {
  if (!s || s._isNew) return 'New Service…';
  const code = effectiveServiceCode(s) || '';
  const desc = String(s.service_description ?? s.description ?? '').trim();
  if (!code && !desc) return '';
  return desc ? `${code || '—'} — ${desc}` : code || '';
}

/**
 * @param {string|number} valueId - services.id or ''
 * @param {(raw: string, service?: object|null) => void} onCommit
 */
export default function LineItemServiceAutocomplete({
  valueId,
  onCommit,
  size = 'small',
  disabled = false,
  label = 'Service',
  showNewService = false,
  textFieldSx,
  noMargin = false
}) {
  const newOpt = useMemo(() => ({ id: NEW_SENT, _isNew: true }), []);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedService, setResolvedService] = useState(null);
  const debouncedInput = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    let cancelled = false;
    if (valueId == null || String(valueId).trim() === '') {
      setResolvedService(null);
      return undefined;
    }
    if (resolvedService && String(resolvedService.id) === String(valueId)) {
      return undefined;
    }
    (async () => {
      const service = await fetchActiveServiceById(valueId);
      if (!cancelled) setResolvedService(service);
    })();
    return () => {
      cancelled = true;
    };
  }, [valueId, resolvedService]);

  useEffect(() => {
    let cancelled = false;
    const q = debouncedInput.trim();
    if (!q) {
      setOptions(resolvedService ? [resolvedService] : []);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const results = await searchActiveServices(q);
        if (cancelled) return;
        setOptions(mergeServiceSearchResults(results, resolvedService));
      } catch (err) {
        console.error('Service search failed:', err);
        if (!cancelled) setOptions(resolvedService ? [resolvedService] : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedInput, resolvedService]);

  const listOptions = useMemo(
    () => (showNewService ? [newOpt, ...options] : [...options]),
    [showNewService, newOpt, options]
  );

  const selected = useMemo(() => {
    if (valueId == null || String(valueId).trim() === '') return null;
    if (resolvedService && String(resolvedService.id) === String(valueId)) return resolvedService;
    return options.find((s) => String(s.id) === String(valueId)) || null;
  }, [valueId, resolvedService, options]);

  return (
    <Autocomplete
      size={size}
      disabled={disabled}
      options={listOptions}
      value={selected}
      loading={loading}
      filterOptions={(opts) => opts}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'reset') return;
        setInputValue(newInputValue);
      }}
      onChange={(_e, v) => {
        if (!v) {
          onCommit('', null);
          return;
        }
        if (v._isNew) {
          onCommit(NEW_SENT, null);
          return;
        }
        setResolvedService(v);
        onCommit(String(v.id), v);
      }}
      getOptionLabel={(opt) => serviceOptionLabel(opt)}
      isOptionEqualToValue={(a, b) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return String(a.id) === String(b.id);
      }}
      noOptionsText={
        loading ? 'Searching…' : inputValue.trim() ? 'No services found' : 'Type to search services'
      }
      renderOption={(props, opt) => (
        <li {...props} key={opt._isNew ? NEW_SENT : opt.id}>
          {serviceOptionLabel(opt)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          // Table cells already have a column header; floating labels bleed into the row above.
          label={noMargin ? undefined : label}
          placeholder="Search services…"
          InputLabelProps={noMargin ? undefined : { ...params.InputLabelProps, shrink: true }}
          inputProps={{
            ...params.inputProps,
            'aria-label': label || 'Service'
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={textFieldSx}
          margin={noMargin ? 'none' : 'dense'}
        />
      )}
      ListboxProps={{ style: { maxHeight: 280 } }}
    />
  );
}
